import React, {useMemo} from 'react';
import {Avatar, Button, Card, Collapse, Divider, Flex, List, Tag, Typography} from "antd";
import {launcherCard} from "../../common/styles";
import {Label} from "../../../commons/translations/Label";
import {
    AudioMutedOutlined,
    DislikeOutlined,
    EditOutlined,
    EyeOutlined, SunOutlined,
    UserOutlined,
    WarningOutlined
} from "@ant-design/icons";
import {ContentWarning, KeyValueIcon} from "../../common/types";
import {Account, SelectableContentWarning} from "../types";
import {translateExperienceValue} from "./utils";

interface PlayerProfileCardProps {
    profile: Account | undefined;
    setEditingProfile: (editing: boolean) => void;
}

export const PlayerProfileCard: React.FC<PlayerProfileCardProps> = (props) => {
    const { profile, setEditingProfile } = props;
    const playingExperience = useMemo<{color: string, value: string}>(() => translateExperienceValue(profile?.profile?.experience), [profile]);
    const triggers = useMemo<SelectableContentWarning[]>(() => profile?.profile?.contentWarnings || [], [profile]);
    const needs = useMemo<KeyValueIcon<boolean>[]>(() => {
        return [
            { key: 'needs.needWheelchairAccess', icon: () => <WarningOutlined/>, value: !!profile?.profile?.needWheelchairAccess},
            { key: 'needs.hearingImpaired', icon: () => <AudioMutedOutlined />, value: !!profile?.profile?.hearingImpaired},
            { key: 'needs.visuallyImpaired', icon: () => <EyeOutlined />, value: !!profile?.profile?.visuallyImpaired},
            { key: 'needs.photosensitivity', icon: () => <SunOutlined />, value: !!profile?.profile?.photosensitivity},
        ];
    }, [profile]);

  return (
      <Card style={{...launcherCard}} title={<Typography.Title level={2}><Label trl="profile.player.title" /></Typography.Title>}>
          <Flex justify={"start"} vertical={true} >
              <Flex justify={"flex-start"} align="end" vertical={false}>
                  <Avatar src={profile?.picture} icon={<UserOutlined/>} size={"large"} />
                  <Typography.Title level={3} style={{marginInlineStart: '10px'}} >{profile?.nickname}</Typography.Title>
              </Flex>
              <Flex style={{margin: '4px'}} vertical={false} justify={"space-between"} align={"start"}>
                  <Typography.Text>{profile?.name}</Typography.Text>
                  <Tag color={"green"} style={{marginLeft: '5px'}}>{profile?.status}</Tag>
                  <Button type={"default"} icon={<EditOutlined />} onClick={() => setEditingProfile(true)} />
              </Flex>
              <Divider />
              <Flex style={{margin: '4px'}} vertical={false} justify={"space-between"} align={"start"}>
                  <Label trl={'profile.experience'} />
                  <Tag color={playingExperience.color} style={{marginLeft: '5px'}}>{playingExperience.value}</Tag>
              </Flex>
              <Divider />
              <Collapse>
                  <Collapse.Panel header={<Label trl="profile.needs.title" />} key={'1'}>
                      <List dataSource={needs.filter(need => need.value)}
                            renderItem={(need) => {
                                return (<Flex justify={"start"} align={"center"}>
                                    {need.icon({})}
                                    <Label trl={need.key} key={need.key} style={{marginLeft: '5px'}} />
                                </Flex>)
                            }}
                      />
                  </Collapse.Panel>
                  <Collapse.Panel header={<Label trl="profile.content.preferences" />} key={'2'}>
                      <List dataSource={triggers}
                            renderItem={(t) => {
                                const trigger = t as ContentWarning;
                                return (<Flex justify={"start"} align={"center"}>
                                    <DislikeOutlined style={{marginRight: '5px', color: 'red'}}/>
                                    <Label trl={trigger.key} key={trigger.key} />
                                </Flex>)
                            }}
                      />
                  </Collapse.Panel>
              </Collapse>
          </Flex>
      </Card>
  );
};
